import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import {
  Shield,
  Smartphone,
  MessageSquare,
  Wrench,
  Check,
  X,
  ChevronDown,
  ArrowRight,
  Phone,
  Star,
  Clock,
  Users,
  Zap,
  Layers,
  HeartHandshake,
  TrendingUp,
  Send,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ───── Hero showcase screens ───── */
const heroScreens = [
  '/portfolio-screenshots/corsica-multicoques.png',
  '/portfolio-screenshots/assureconseil.png',
  '/portfolio-screenshots/ajaccio-catamaran.png',
  '/portfolio-screenshots/soprel-groupe.png',
  '/portfolio-screenshots/differenza-new.png',
  '/portfolio-screenshots/blusognu.png',
  '/portfolio-screenshots/imagine-decoration.png',
];

/* ───── Benefits ───── */
const benefits = [
  { icon: Shield, title: 'Une image professionnelle', desc: 'Rassurez vos futurs clients avec un site soigné qui reflète le sérieux de votre activité.' },
  { icon: Smartphone, title: 'Lisible partout', desc: 'Votre site s\'affiche parfaitement sur smartphone, tablette et ordinateur.' },
  { icon: MessageSquare, title: 'Facile de vous contacter', desc: 'Téléphone, formulaire, demande de devis : vos clients vous trouvent en un clic.' },
  { icon: Wrench, title: 'Zéro prise de tête technique', desc: 'On s\'occupe de tout. Vous n\'avez besoin d\'aucune compétence informatique.' },
];

/* ───── Pricing plans ───── */
const plans = [
  {
    name: 'Essentiel',
    price: 348,
    monthly: 34.80,
    badge: null,
    desc: 'Une présence professionnelle simple et accessible.',
    features: [
      'Site vitrine d\'une page',
      'Design responsive',
      'Présentation de votre activité',
      'Coordonnées, horaires et itinéraire',
      'Boutons d\'appel et de contact',
      'Référencement technique de base',
      'Hébergement et maintenance inclus',
    ],
    cta: 'Choisir Essentiel',
    accent: false,
  },
  {
    name: 'Business',
    price: 588,
    monthly: 46.80,
    badge: 'Recommandé',
    desc: 'Le meilleur équilibre pour les artisans, commerçants et TPE.',
    features: [
      'Site vitrine de 3 à 5 pages',
      'Design personnalisé',
      'Présentation détaillée des prestations',
      'Formulaire de contact / devis',
      'Galerie de réalisations',
      'Référencement local de base',
      'Accompagnement à la mise en ligne',
      'Hébergement, maintenance et assistance',
    ],
    cta: 'Choisir Business',
    accent: true,
  },
  {
    name: 'Premium',
    price: 1188,
    monthly: 70.80,
    badge: null,
    desc: 'Une présence web complète et personnalisée.',
    features: [
      'Site vitrine jusqu\'à 8 pages',
      'Design approfondi sur-mesure',
      'Architecture de contenu adaptée',
      'Galerie avancée / portfolio',
      'Référencement initial renforcé',
      'Intégrations complémentaires',
      'Accompagnement complet',
      'Hébergement, maintenance et assistance',
    ],
    cta: 'Demander un devis Premium',
    accent: false,
  },
];

/* ───── Comparison table ───── */
const comparisonRows = [
  { label: 'Nombre de pages', e: '1', b: '3 à 5', p: 'Jusqu\'à 8' },
  { label: 'Personnalisation', e: 'Base', b: 'Personnalisé', p: 'Sur-mesure' },
  { label: 'Formulaire de contact', e: false, b: true, p: true },
  { label: 'Galerie / réalisations', e: false, b: true, p: true },
  { label: 'Référencement SEO initial', e: 'Basique', b: 'Local', p: 'Renforcé' },
  { label: 'Hébergement', e: true, b: true, p: true },
  { label: 'Maintenance', e: true, b: true, p: true },
  { label: 'Assistance', e: 'Email', b: 'Email + Tél.', p: 'Prioritaire' },
  { label: 'Prix de création', e: '348 € TTC', b: '588 € TTC', p: '1 188 € TTC' },
  { label: 'Abonnement mensuel', e: '34,80 € TTC/mois', b: '46,80 € TTC/mois', p: '70,80 € TTC/mois' },
];

/* ───── Steps ───── */
const steps = [
  { num: '01', title: 'Vous nous expliquez', desc: 'Votre activité, vos besoins, vos objectifs. On écoute et on vous conseille.' },
  { num: '02', title: 'On prépare votre site', desc: 'Vous fournissez vos infos, textes et photos. On construit votre site.' },
  { num: '03', title: 'Vous validez', desc: 'On vous présente le résultat. Les ajustements prévus sont effectués.' },
  { num: '04', title: 'Votre site est en ligne', desc: 'Publication et accompagnement technique. Objectif : 7 jours après réception de vos contenus.' },
];

/* ───── Why us ───── */
const whyUs = [
  { icon: Layers, title: 'Tarifs clairs', desc: 'Pas de surprise. Vous savez exactement ce que vous payez.' },
  { icon: Zap, title: 'Prestations définies', desc: 'Chaque formule détaille précisément ce qui est inclus.' },
  { icon: HeartHandshake, title: 'Adapté aux petites entreprises', desc: 'On connaît vos contraintes et on s\'y adapte.' },
  { icon: Users, title: 'Un interlocuteur identifié', desc: 'Vous parlez à une vraie personne, pas à un chatbot.' },
  { icon: TrendingUp, title: 'Solution évolutive', desc: 'Votre site peut grandir avec votre activité.' },
  { icon: MessageSquare, title: 'Sans jargon technique', desc: 'On parle votre langue, pas celle des développeurs.' },
];

/* ───── FAQ ───── */
const faqItems = [
  { q: 'Le nom de domaine est-il compris ?', a: 'Le nom de domaine peut être inclus ou géré séparément selon la formule. Les frais annuels de renouvellement du domaine sont à la charge du client. Nous vous accompagnons dans le choix et la configuration.' },
  { q: 'Puis-je utiliser mon propre nom de domaine ?', a: 'Oui, tout à fait. Si vous possédez déjà un nom de domaine, nous le configurerons pour pointer vers votre nouveau site.' },
  { q: 'Qui fournit les textes et les photos ?', a: 'C\'est vous qui fournissez les contenus (textes, photos, logo). Nous pouvons vous guider et vous conseiller sur la rédaction et les visuels adaptés.' },
  { q: 'Puis-je modifier mon site après sa mise en ligne ?', a: 'Oui. Le nombre de modifications incluses dépend de votre formule. Les modifications supplémentaires peuvent être réalisées sur devis.' },
  { q: 'Que comprend exactement l\'abonnement mensuel ?', a: 'L\'abonnement couvre l\'hébergement, la maintenance technique, les mises à jour de sécurité et un niveau d\'assistance défini selon votre formule.' },
  { q: 'Puis-je résilier mon abonnement ?', a: 'Les modalités de résiliation seront précisées dans votre contrat. Nous privilégions la transparence et la flexibilité.' },
  { q: 'Mon site sera-t-il adapté aux smartphones ?', a: 'Oui, tous nos sites sont conçus en responsive design : ils s\'adaptent automatiquement à tous les écrans (smartphone, tablette, ordinateur).' },
  { q: 'Le référencement sur Google est-il garanti ?', a: 'Nous mettons en place les bonnes pratiques techniques de référencement (SEO). Cependant, aucun prestataire honnête ne peut garantir un positionnement précis sur Google, car cela dépend de nombreux facteurs.' },
  { q: 'Puis-je ajouter des fonctionnalités par la suite ?', a: 'Oui. Votre site peut évoluer : ajout de pages, fonctionnalités supplémentaires, intégrations. Nous étudions vos besoins sur devis.' },
  { q: 'Combien de temps faut-il pour créer mon site ?', a: 'Notre objectif est de 7 jours après réception de l\'ensemble de vos contenus et validations. Ce délai peut varier selon la complexité du projet et votre réactivité.' },
];

/* ───── Animations ───── */
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
};

/* ═══════════════ COMPONENTS ═══════════════ */

const ComparisonCell = ({ val }) => {
  if (val === true) return <Check className="text-green-400 mx-auto" size={18} />;
  if (val === false) return <X className="text-white/20 mx-auto" size={18} />;
  return <span className="text-white/70 text-sm">{val}</span>;
};

const FaqItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/5">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left gap-4"
        data-testid={`faq-toggle`}
      >
        <span className="text-white font-medium text-sm sm:text-base">{item.q}</span>
        <ChevronDown
          size={18}
          className={`text-[#8B5A8B] flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="text-white/50 text-sm pb-5 leading-relaxed">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════ MAIN PAGE ═══════════════ */

export const HomePage = () => {
  const [heroIdx, setHeroIdx] = useState(0);
  const [formData, setFormData] = useState({
    name: '', company: '', activity: '', email: '', phone: '', formula: '', message: ''
  });
  const [formStatus, setFormStatus] = useState(null);
  const [sending, setSending] = useState(false);

  React.useEffect(() => {
    const t = setInterval(() => setHeroIdx(i => (i + 1) % heroScreens.length), 3500);
    return () => clearInterval(t);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile_type: 'prospect',
          name: formData.name,
          email: formData.email,
          phone: formData.phone || '',
          company: formData.company,
          project_type: formData.formula || 'Non précisé',
          message: `Activité: ${formData.activity}\nFormule souhaitée: ${formData.formula || 'Non précisée'}\n\n${formData.message || ''}`,
        }),
      });
      if (res.ok) {
        setFormStatus('success');
        setFormData({ name: '', company: '', activity: '', email: '', phone: '', formula: '', message: '' });
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="home-page">
      <SEO
        title="Création de sites internet pour artisans, commerçants et TPE"
        description="Un site internet professionnel, moderne et adapté aux mobiles pour développer votre visibilité. Création à partir de 348€ TTC. Devis gratuit."
        path="/"
      />

      {/* ═══════ SECTION 1 — HERO ═══════ */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 hero-grid-pattern opacity-[0.02]" />
        <div className="absolute top-1/3 right-1/4 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-[#8B5A8B]/8 rounded-full blur-[150px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0C0514] to-transparent z-10 pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-36 pb-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            {/* Text */}
            <motion.div variants={stagger} initial="hidden" animate="visible">
              <motion.div variants={fadeUp} className="mb-6">
                <span className="inline-flex items-center gap-2 border border-[#8B5A8B]/30 text-white/70 text-xs px-4 py-2 rounded-full uppercase tracking-wider">
                  <Clock size={14} className="text-[#8B5A8B]" />
                  Objectif : en ligne sous 7 jours
                </span>
              </motion.div>

              <motion.h1 variants={fadeUp} className="text-[2.5rem] sm:text-5xl lg:text-[3.5rem] font-black tracking-tight leading-[1.08] text-white mb-6" data-testid="hero-title">
                Votre site web professionnel,{' '}
                <span className="text-[#8B5A8B]">sans vous ruiner</span>
              </motion.h1>

              <motion.p variants={fadeUp} className="text-base lg:text-lg text-white/50 max-w-lg mb-8 leading-relaxed" data-testid="hero-subtitle">
                Un site moderne, adapté aux mobiles et pensé pour votre activité. 
                Nous nous occupons de la création, vous vous concentrez sur votre métier.
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-3 mb-10">
                {['Dès 348 € TTC', '1 interlocuteur dédié', 'Accompagnement complet'].map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 text-white/60 text-sm">
                    <Check size={14} className="text-[#8B5A8B]" /> {t}
                  </span>
                ))}
              </motion.div>

              <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
                <a href="#offres" className="glow-btn inline-flex items-center justify-center gap-2" data-testid="hero-cta-primary">
                  Découvrir nos offres
                  <ArrowRight size={18} />
                </a>
                <a href="#contact" className="outline-btn inline-flex items-center justify-center gap-2" data-testid="hero-cta-secondary">
                  Obtenir mon devis gratuit
                </a>
              </motion.div>
            </motion.div>

            {/* Screens showcase */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="relative"
              data-testid="hero-mockups"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-[#8B5A8B]/15 border border-white/10">
                <div className="relative w-full aspect-[16/10]">
                  {heroScreens.map((src, i) => (
                    <img
                      key={src}
                      src={src}
                      alt="Exemple de site professionnel"
                      className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-1000"
                      style={{ opacity: i === heroIdx ? 1 : 0 }}
                    />
                  ))}
                </div>
              </div>
              <div className="absolute -inset-6 bg-[#8B5A8B]/10 blur-[60px] rounded-full -z-10" />
              {/* Floating badge */}
              <div className="absolute -bottom-4 -right-4 sm:right-4 glass-card rounded-xl px-4 py-3 flex items-center gap-3 z-20">
                <div className="w-10 h-10 rounded-full bg-[#8B5A8B]/20 flex items-center justify-center">
                  <Star className="text-[#8B5A8B]" size={18} />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">+25 sites créés</p>
                  <p className="text-white/40 text-xs">pour les pros comme vous</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ SECTION 2 — BÉNÉFICES ═══════ */}
      <section className="py-24 px-6 lg:px-12" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
            <p className="section-label mb-4">Pourquoi un site web ?</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">
              Ce qu'un site fait <span className="text-[#8B5A8B]">concrètement</span> pour vous
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card rounded-2xl p-7 text-center"
                data-testid={`benefit-${i}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-[#8B5A8B]/10 border border-[#8B5A8B]/20 flex items-center justify-center mx-auto mb-5">
                  <b.icon className="text-[#8B5A8B]" size={24} />
                </div>
                <h3 className="text-white font-bold mb-2">{b.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ SECTION 3 — OFFRES ═══════ */}
      <section id="offres" className="py-24 px-6 lg:px-12 scroll-mt-28" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
            <p className="section-label mb-4">Nos offres</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">
              Trois formules, <span className="text-[#8B5A8B]">un seul objectif</span>
            </h2>
            <p className="text-white/50 mt-4 max-w-xl mx-auto">Vous rendre visible. Choisissez la formule qui correspond à votre activité et votre budget.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className={`glass-card rounded-2xl p-8 relative flex flex-col ${plan.accent ? 'border-[#8B5A8B]/40 shadow-[0_0_40px_rgba(139,90,139,0.15)]' : ''}`}
                data-testid={`plan-${plan.name.toLowerCase()}`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#8B5A8B] text-white text-xs font-semibold px-4 py-1 rounded-full">
                    {plan.badge}
                  </div>
                )}
                <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                <p className="text-white/40 text-sm mb-5">{plan.desc}</p>
                <div className="mb-1">
                  <span className="text-4xl font-black text-white">{plan.price}</span>
                  <span className="text-white/50 text-lg ml-1">€ TTC</span>
                </div>
                <p className="text-[#8B5A8B] text-sm font-medium mb-6">puis {plan.monthly} € TTC/mois</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <Check className="text-[#8B5A8B] flex-shrink-0 mt-0.5" size={15} />
                      <span className="text-white/60 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className={plan.accent ? 'glow-btn text-center text-sm w-full' : 'outline-btn text-center text-sm w-full'}
                  data-testid={`plan-cta-${plan.name.toLowerCase()}`}
                >
                  {plan.cta}
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ SECTION 4 — COMPARATIF ═══════ */}
      <section className="py-24 px-6 lg:px-12" data-testid="comparison-section">
        <div className="max-w-4xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
            <p className="section-label mb-4">Comparatif</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">
              Comparez les <span className="text-[#8B5A8B]">formules</span>
            </h2>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="glass-card rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]" data-testid="comparison-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-white/40 text-xs uppercase tracking-wider w-2/5"></th>
                    <th className="p-4 text-white font-bold text-sm">Essentiel</th>
                    <th className="p-4 text-[#8B5A8B] font-bold text-sm">Business</th>
                    <th className="p-4 text-white font-bold text-sm">Premium</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="p-4 text-white/60 text-sm">{row.label}</td>
                      <td className="p-4 text-center"><ComparisonCell val={row.e} /></td>
                      <td className="p-4 text-center bg-[#8B5A8B]/5"><ComparisonCell val={row.b} /></td>
                      <td className="p-4 text-center"><ComparisonCell val={row.p} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════ SECTION 5 — MÉTHODE ═══════ */}
      <section className="py-24 px-6 lg:px-12" data-testid="method-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
            <p className="section-label mb-4">Comment ça marche</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">
              Quatre étapes, <span className="text-[#8B5A8B]">c'est tout</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card rounded-2xl p-7 relative"
                data-testid={`step-${i}`}
              >
                <span className="text-5xl font-black text-[#8B5A8B]/10 absolute top-4 right-6 select-none">{step.num}</span>
                <div className="relative z-10">
                  <p className="text-[#8B5A8B] text-xs font-semibold uppercase tracking-wider mb-3">Étape {step.num}</p>
                  <h3 className="text-white font-bold text-lg mb-2">{step.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-white/40 text-sm mt-8"
          >
            * Le délai de 7 jours est un objectif indicatif, conditionné à la réception de vos contenus et validations.
          </motion.p>
        </div>
      </section>

      {/* ═══════ SECTION 6 — POURQUOI NOUS ═══════ */}
      <section className="py-24 px-6 lg:px-12" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
            <p className="section-label mb-4">Nos engagements</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">
              Pourquoi nous <span className="text-[#8B5A8B]">choisir</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whyUs.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="glass-card rounded-2xl p-7"
                data-testid={`why-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#8B5A8B]/10 border border-[#8B5A8B]/20 flex items-center justify-center mb-4">
                  <item.icon className="text-[#8B5A8B]" size={20} />
                </div>
                <h3 className="text-white font-bold mb-1">{item.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ SECTION 7 — FAQ ═══════ */}
      <section className="py-24 px-6 lg:px-12" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
            <p className="section-label mb-4">FAQ</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">
              Questions <span className="text-[#8B5A8B]">fréquentes</span>
            </h2>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="glass-card rounded-2xl px-6 sm:px-8">
            {faqItems.map((item, i) => (
              <FaqItem key={i} item={item} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════ SECTION 8 — CTA + FORMULAIRE ═══════ */}
      <section id="contact" className="py-24 px-6 lg:px-12 scroll-mt-28" data-testid="cta-section">
        <div className="max-w-5xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white mb-4">
              Prêt à donner une nouvelle dimension <br className="hidden sm:block" />
              <span className="text-[#8B5A8B]">à votre activité ?</span>
            </h2>
            <p className="text-white/50 text-base max-w-xl mx-auto leading-relaxed">
              Présentez-nous votre projet. Nous vous aiderons à choisir la formule adaptée à votre activité et à votre budget.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-8 sm:p-10"
          >
            {formStatus === 'success' ? (
              <div className="text-center py-10" data-testid="form-success">
                <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto mb-6">
                  <Check className="text-green-400" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Message envoyé !</h3>
                <p className="text-white/50">Nous reviendrons vers vous dans les plus brefs délais.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="text-white/60 text-sm mb-1.5 block">Nom *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[#8B5A8B]/50 focus:outline-none transition-colors"
                      placeholder="Votre nom"
                      data-testid="form-name"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-sm mb-1.5 block">Entreprise</label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={e => setFormData({ ...formData, company: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[#8B5A8B]/50 focus:outline-none transition-colors"
                      placeholder="Nom de votre entreprise"
                      data-testid="form-company"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-sm mb-1.5 block">Activité *</label>
                    <input
                      type="text"
                      required
                      value={formData.activity}
                      onChange={e => setFormData({ ...formData, activity: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[#8B5A8B]/50 focus:outline-none transition-colors"
                      placeholder="ex : Plombier, Restaurant, Coiffeur..."
                      data-testid="form-activity"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-sm mb-1.5 block">Adresse e-mail *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[#8B5A8B]/50 focus:outline-none transition-colors"
                      placeholder="votre@email.com"
                      data-testid="form-email"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-sm mb-1.5 block">Téléphone <span className="text-white/30">(facultatif)</span></label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[#8B5A8B]/50 focus:outline-none transition-colors"
                      placeholder="06 XX XX XX XX"
                      data-testid="form-phone"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-sm mb-1.5 block">Formule souhaitée</label>
                    <select
                      value={formData.formula}
                      onChange={e => setFormData({ ...formData, formula: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[#8B5A8B]/50 focus:outline-none transition-colors appearance-none"
                      data-testid="form-formula"
                    >
                      <option value="" className="bg-[#140B1E]">Je ne sais pas encore</option>
                      <option value="Essentiel" className="bg-[#140B1E]">Essentiel — 348 € TTC</option>
                      <option value="Business" className="bg-[#140B1E]">Business — 588 € TTC</option>
                      <option value="Premium" className="bg-[#140B1E]">Premium — 1 188 € TTC</option>
                    </select>
                  </div>
                </div>
                <div className="mb-6">
                  <label className="text-white/60 text-sm mb-1.5 block">Décrivez votre projet <span className="text-white/30">(facultatif)</span></label>
                  <textarea
                    value={formData.message}
                    onChange={e => setFormData({ ...formData, message: e.target.value })}
                    rows={3}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[#8B5A8B]/50 focus:outline-none transition-colors resize-none"
                    placeholder="Parlez-nous de votre activité et de vos attentes..."
                    data-testid="form-message"
                  />
                </div>
                <p className="text-white/30 text-xs mb-6">
                  Vos données sont utilisées uniquement pour répondre à votre demande. Nous ne les partageons avec aucun tiers.
                </p>
                {formStatus === 'error' && (
                  <p className="text-red-400 text-sm mb-4">Une erreur est survenue. Veuillez réessayer ou nous contacter par téléphone.</p>
                )}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    type="submit"
                    disabled={sending}
                    className="glow-btn inline-flex items-center justify-center gap-2 flex-1"
                    data-testid="form-submit"
                  >
                    <Send size={18} />
                    {sending ? 'Envoi en cours...' : 'Obtenir mon devis gratuit'}
                  </button>
                  <a
                    href="tel:0759948289"
                    className="outline-btn inline-flex items-center justify-center gap-2 flex-1"
                    data-testid="form-call"
                  >
                    <Phone size={18} />
                    Être rappelé
                  </a>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
